import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GP_STATUS_LABEL, GP_STATUS_COLOR } from "@/config/gatepass";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AdminActionDialog } from "@/components/AdminActionDialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { key: "draft", label: "Draft" },
  { key: "pending_verification", label: "Verification" },
  { key: "pending_authorization", label: "Final Authorization" },
  { key: "issued", label: "Issued" },
];

export default function GatePassDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can, user, isAdmin } = useAuth();
  const [g, setG] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});
  const [busy, setBusy] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [designation, setDesignation] = useState("");
  const [authDesignation, setAuthDesignation] = useState("BSP Manager");
  const [cancelReason, setCancelReason] = useState("");
  const [createReplacement, setCreateReplacement] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/gate-passes/${id}`).then((r) => setG(r.data)).catch(() => setG(null)).finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {}); }, [load]);

  if (loading) return <div className="flex justify-center mt-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (!g) return <div className="text-center mt-20 text-muted-foreground">Gate pass not found.</div>;

  const purpose = g.purpose === "Other" ? g.purpose_other : g.purpose;
  const stepIdx = STEPS.findIndex((s) => s.key === g.status);

  const act = async (fn) => { setBusy(true); try { await fn(); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed"); } finally { setBusy(false); } };

  const doVerify = () => act(async () => { await api.post(`/gate-passes/${id}/verify`, { designation }); toast.success("Verified"); setVerifyOpen(false); });
  const doAuthorize = () => act(async () => { const r = await api.post(`/gate-passes/${id}/authorize`, { designation: authDesignation }); toast.success(`Issued as ${r.data.gate_pass_number}`); setAuthOpen(false); });
  const doCancel = () => act(async () => { const r = await api.post(`/gate-passes/${id}/cancel`, { reason: cancelReason, create_replacement: createReplacement }); toast.success("Gate pass cancelled"); setCancelOpen(false); if (r.data.replacement_id) navigate(`/gate-pass/${r.data.replacement_id}/edit`); });
  const doDelete = () => act(async () => { await api.delete(`/gate-passes/${id}`); toast.success("Draft deleted"); navigate("/gate-pass"); });
  const doSubmit = () => act(async () => { await api.post(`/gate-passes/${id}/submit`); toast.success("Submitted for verification"); });
  const doVoid = (reason) => act(async () => { await api.post(`/gate-passes/${id}/void`, { reason }); toast.success("Gate pass voided"); setVoidOpen(false); });
  const doTestFlag = (reason) => act(async () => { await api.post(`/gate-passes/${id}/test-flag`, { is_test: !g.is_test, reason }); toast.success(g.is_test ? "Marked as production" : "Marked as test"); setTestOpen(false); });

  const downloadPdf = async () => {
    try { const res = await api.get(`/gate-passes/${id}/pdf`, { responseType: "blob" }); const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `${(g.gate_pass_number || "gate_pass_draft").replace(/\//g, "_")}.pdf`; a.click(); URL.revokeObjectURL(url); } catch { toast.error("Could not generate PDF"); }
  };
  const printPdf = async () => {
    try { const res = await api.get(`/gate-passes/${id}/pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank"); } catch { toast.error("Could not open PDF"); }
  };

  const Field = ({ label, value }) => (
    <div><div className="overline text-muted-foreground">{label}</div><div className="text-sm font-medium">{value || "—"}</div></div>
  );

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/gate-pass")} data-testid="gp-back-btn"><Icons.ArrowLeft className="h-5 w-5" /></Button>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="gp-number">{g.gate_pass_number || "Draft Gate Pass"}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge variant="secondary" className={GP_STATUS_COLOR[g.status]} data-testid="gp-status">{GP_STATUS_LABEL[g.status]}</Badge>
              {g.is_test && <Badge variant="secondary" className="bg-amber-100 text-amber-800" data-testid="gp-test-badge">TEST</Badge>}
              {g.is_void && <Badge variant="secondary" className="bg-red-100 text-red-800" data-testid="gp-void-badge">VOID</Badge>}
              {g.issue_date && <span className="text-xs text-muted-foreground">{g.issue_date} · {g.issue_time} IST</span>}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {can("gate_pass", "download") && <Button variant="outline" className="gap-1.5" onClick={printPdf} data-testid="gp-print-btn"><Icons.Printer className="h-4 w-4" />Print</Button>}
          {can("gate_pass", "download") && <Button variant="outline" className="gap-1.5" onClick={downloadPdf} data-testid="gp-pdf-btn"><Icons.FileDown className="h-4 w-4" />Download PDF</Button>}
          {g.status === "draft" && isAdmin && <Button variant="outline" className="gap-1.5" onClick={() => navigate(`/gate-pass/${id}/edit`)} data-testid="gp-edit-btn"><Icons.Pencil className="h-4 w-4" />Edit</Button>}
        </div>
      </div>

      {/* Workflow stepper */}
      {g.status !== "cancelled" && (
        <Card className="p-4">
          <div className="flex items-center justify-between">
            {STEPS.map((s, i) => (
              <div key={s.key} className="flex-1 flex items-center">
                <div className="flex flex-col items-center gap-1">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${i <= stepIdx ? "text-white" : "bg-secondary text-muted-foreground"}`} style={i <= stepIdx ? { backgroundColor: "#1e3a5f" } : {}}>
                    {i < stepIdx ? <Icons.Check className="h-4 w-4" /> : i + 1}
                  </div>
                  <span className={`text-[11px] ${i <= stepIdx ? "font-medium" : "text-muted-foreground"}`}>{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${i < stepIdx ? "" : "bg-secondary"}`} style={i < stepIdx ? { backgroundColor: "#1e3a5f" } : {}} />}
              </div>
            ))}
          </div>
        </Card>
      )}

      {g.status === "cancelled" && (
        <Card className="p-4 border-destructive/40 bg-destructive/5">
          <div className="flex items-center gap-2 text-destructive font-semibold"><Icons.Ban className="h-5 w-5" />Cancelled</div>
          <p className="text-sm mt-1">Reason: {g.cancel_reason}</p>
          <p className="text-xs text-muted-foreground mt-1">by {g.cancelled_by_name} · {g.cancelled_at}</p>
          {g.replacement_id && <Button variant="link" className="px-0 h-auto" onClick={() => navigate(`/gate-pass/${g.replacement_id}`)}>View replacement gate pass →</Button>}
        </Card>
      )}

      {g.is_void && (
        <Card className="p-4 border-red-300 bg-red-50" data-testid="gp-void-banner">
          <div className="flex items-center gap-2 text-red-700 font-semibold"><Icons.Ban className="h-5 w-5" />Voided Record</div>
          <p className="text-sm mt-1">Reason: {g.void_reason}</p>
          <p className="text-xs text-muted-foreground mt-1">by {g.voided_by_name} · {g.voided_at} — excluded from active registers &amp; dashboard totals, kept for audit.</p>
        </Card>
      )}

      {/* Details */}
      <Card className="p-5">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Field label="Movement Type" value={g.movement_type} />
          <Field label="Purpose" value={purpose} />
          <Field label="Party / Customer" value={g.party_name} />
          <Field label="Vehicle No." value={g.vehicle_number} />
          <Field label="Driver Name" value={g.driver_name} />
          <Field label="Driver Mobile" value={g.driver_mobile} />
          <Field label="Bill / Invoice No." value={g.bill_number} />
          <Field label="Created By" value={g.created_by} />
        </div>
        {g.remarks && <div className="mt-4"><Field label="Remarks" value={g.remarks} /></div>}
      </Card>

      {/* Materials */}
      <Card className="p-5">
        <h3 className="font-display font-semibold mb-3">Material Details</h3>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>{["Sl.", "Description", "Quantity", "Unit", "Remarks"].map((h) => <TableHead key={h} className="overline text-muted-foreground">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {(g.materials || []).map((m, i) => (
                <TableRow key={i}><TableCell>{i + 1}</TableCell><TableCell>{m.description}</TableCell><TableCell>{m.quantity ?? "—"}</TableCell><TableCell>{m.unit || "—"}</TableCell><TableCell>{m.remarks || "—"}</TableCell></TableRow>
              ))}
              {(!g.materials || g.materials.length === 0) && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No materials</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Verification / Authorization */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <h3 className="font-display font-semibold mb-3">Verified By</h3>
          {g.verified_by_name ? (
            <div className="space-y-1 text-sm">
              <div><span className="text-muted-foreground">Name:</span> <span className="font-medium">{g.verified_by_name}</span></div>
              <div><span className="text-muted-foreground">Designation:</span> {g.verified_by_designation || "—"}</div>
              <div className="text-xs text-muted-foreground">{g.verified_at}</div>
            </div>
          ) : <p className="text-sm text-muted-foreground">Awaiting verification.</p>}
        </Card>
        <Card className="p-5 border-2" style={{ borderColor: "#1e3a5f" }}>
          <h3 className="font-display font-semibold mb-3" style={{ color: "#1e3a5f" }}>FINAL AUTHORIZATION</h3>
          {g.authorized_by_name ? (
            <div className="space-y-1 text-sm">
              <div><span className="text-muted-foreground">Authorized By:</span> <span className="font-medium">{g.authorized_by_name}</span></div>
              <div><span className="text-muted-foreground">Designation:</span> {g.authorized_by_designation || "BSP Manager"}</div>
              <div className="text-xs text-muted-foreground">{g.authorized_at}</div>
            </div>
          ) : <p className="text-sm text-muted-foreground">Awaiting final authorization by BSP Manager.</p>}
        </Card>
      </div>

      {/* Gatekeeper QR (issued only) */}
      {g.status === "issued" && g.gate_pass_number && (
        <Card className="p-5 flex flex-col sm:flex-row items-center gap-5" data-testid="gp-qr-card">
          <div className="bg-white p-3 rounded-lg border border-border">
            <QRCodeSVG value={`${window.location.origin}/verify/${g.gate_pass_number}`} size={132} fgColor="#1e3a5f" />
          </div>
          <div>
            <h3 className="font-display font-semibold flex items-center gap-2"><Icons.QrCode className="h-4 w-4" style={{ color: "#1e3a5f" }} />Gatekeeper Verification</h3>
            <p className="text-sm text-muted-foreground mt-1">Security can scan this code at the gate to instantly confirm this pass is genuine. The same QR is printed on the PDF gate pass.</p>
            <Button variant="outline" size="sm" className="mt-3 gap-1.5" onClick={() => navigate(`/gate-pass/verify/${g.gate_pass_number}`)} data-testid="gp-open-verify">
              <Icons.ShieldCheck className="h-4 w-4" />Open Verify Page
            </Button>
          </div>
        </Card>
      )}

      {/* Workflow action bar */}
      <Card className="p-4 flex flex-wrap gap-3 items-center">
        {g.status === "draft" && can("gate_pass", "create") && (
          <>
            <Button onClick={doSubmit} disabled={busy} className="gap-1.5" data-testid="gp-action-submit"><Icons.Send className="h-4 w-4" />Submit for Verification</Button>
            {isAdmin && <Button variant="outline" className="text-destructive gap-1.5" onClick={doDelete} disabled={busy} data-testid="gp-action-delete"><Icons.Trash2 className="h-4 w-4" />Delete Draft</Button>}
          </>
        )}
        {g.status === "pending_verification" && can("gate_pass", "verify") && (
          <Button onClick={() => setVerifyOpen(true)} disabled={busy} className="gap-1.5" data-testid="gp-action-verify"><Icons.BadgeCheck className="h-4 w-4" />Verify</Button>
        )}
        {g.status === "pending_authorization" && isAdmin && (
          <Button onClick={() => setAuthOpen(true)} disabled={busy} className="gap-1.5" style={{ backgroundColor: "#1e3a5f" }} data-testid="gp-action-authorize"><Icons.ShieldCheck className="h-4 w-4" />Final Authorize &amp; Issue</Button>
        )}
        {g.status === "issued" && can("gate_pass", "cancel") && (
          <Button variant="outline" className="text-destructive gap-1.5" onClick={() => setCancelOpen(true)} disabled={busy} data-testid="gp-action-cancel"><Icons.Ban className="h-4 w-4" />Cancel Gate Pass</Button>
        )}
        {g.status === "issued" && <span className="text-sm text-emerald-600 flex items-center gap-1.5"><Icons.Lock className="h-4 w-4" />This gate pass is issued and permanently locked.</span>}
        {g.status === "pending_verification" && !can("gate_pass", "verify") && <span className="text-sm text-muted-foreground">Awaiting verification by an authorized user.</span>}
        {g.status === "pending_authorization" && !isAdmin && <span className="text-sm text-muted-foreground">Awaiting final authorization by an administrator.</span>}
      </Card>

      {/* Admin controls */}
      {isAdmin && (
        <Card className="p-4 border-amber-300 bg-amber-50/60" data-testid="gp-admin-controls">
          <div className="flex items-center gap-2 mb-3 text-amber-800"><Icons.ShieldAlert className="h-4 w-4" /><span className="font-semibold text-sm">Admin Controls</span></div>
          <div className="flex flex-wrap gap-2">
            {!g.is_void && g.status !== "issued" && g.status !== "cancelled" && (
              <Button variant="outline" className="text-destructive gap-1.5" onClick={() => setVoidOpen(true)} disabled={busy} data-testid="gp-admin-void"><Icons.Ban className="h-4 w-4" />Void Record</Button>
            )}
            <Button variant="outline" className="gap-1.5" onClick={() => setTestOpen(true)} disabled={busy} data-testid="gp-admin-test"><Icons.FlaskConical className="h-4 w-4" />{g.is_test ? "Unmark Test" : "Mark as Test"}</Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Each action requires a reason and is written to the audit log with your name and time.</p>
        </Card>
      )}

      {/* Audit log */}
      <Card className="p-5">
        <h3 className="font-display font-semibold mb-3">Audit Trail</h3>
        <div className="space-y-2.5">
          {(g.status_history || []).map((h, i) => (
            <div key={i} className="flex items-start gap-2.5 text-sm">
              <div className="h-1.5 w-1.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: "#1e3a5f" }} />
              <div>
                <span className="font-medium">{GP_STATUS_LABEL[h.status] || h.status}</span> — {h.note}
                <div className="text-[11px] text-muted-foreground">{h.by_name} · {h.at}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Verify dialog */}
      <Dialog open={verifyOpen} onOpenChange={setVerifyOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Verify Gate Pass</DialogTitle><DialogDescription>Confirm the details are correct. You are verifying as {user?.name}.</DialogDescription></DialogHeader>
          <div className="space-y-1.5"><Label className="overline text-muted-foreground">Your Designation</Label><Input value={designation} onChange={(e) => setDesignation(e.target.value)} placeholder="e.g. Store In-charge" data-testid="gp-verify-designation" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifyOpen(false)}>Cancel</Button>
            <Button onClick={doVerify} disabled={busy} data-testid="gp-verify-confirm">Confirm Verification</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Authorize dialog */}
      <Dialog open={authOpen} onOpenChange={setAuthOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Final Authorization</DialogTitle><DialogDescription>This will issue the gate pass with a permanent number and lock it. Authorizing as {user?.name}.</DialogDescription></DialogHeader>
          <div className="space-y-1.5"><Label className="overline text-muted-foreground">Designation</Label><Input value={authDesignation} onChange={(e) => setAuthDesignation(e.target.value)} data-testid="gp-auth-designation" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAuthOpen(false)}>Cancel</Button>
            <Button onClick={doAuthorize} disabled={busy} style={{ backgroundColor: "#1e3a5f" }} data-testid="gp-auth-confirm">Authorize &amp; Issue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cancel Gate Pass</DialogTitle><DialogDescription>The original issued record is preserved. Its number is never reused.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="overline text-muted-foreground">Reason for Cancellation *</Label><Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={3} data-testid="gp-cancel-reason" /></div>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={createReplacement} onCheckedChange={(v) => setCreateReplacement(!!v)} data-testid="gp-cancel-replacement" />Create a replacement draft from this gate pass</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>Back</Button>
            <Button className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={doCancel} disabled={busy || !cancelReason.trim()} data-testid="gp-cancel-confirm">Confirm Cancellation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AdminActionDialog open={voidOpen} onOpenChange={setVoidOpen} title="Void Gate Pass"
        description="The record is kept for auditing but excluded from active registers and dashboard totals."
        confirmLabel="Void Record" destructive loading={busy} onConfirm={doVoid} />
      <AdminActionDialog open={testOpen} onOpenChange={setTestOpen}
        title={g.is_test ? "Unmark as Test" : "Mark as Test"}
        description={g.is_test ? "This record will count as a production record again." : "This record will be flagged as test data and excluded from production totals."}
        confirmLabel={g.is_test ? "Unmark Test" : "Mark as Test"} loading={busy} onConfirm={doTestFlag} />
    </div>
  );
}
